import React from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { Globe, Sparkles, ListChecks } from "lucide-react";
import { useApp } from "@/contexts/AppContext";
import { NAV } from "@/constants/testIds";

const navLinks = (t) => [
  { to: "/", label: t.nav.home, key: NAV.linkHome },
  { to: "/scenarios", label: t.nav.scenarios, key: NAV.linkScenarios },
  { to: "/builder", label: t.nav.builder, key: NAV.linkBuilder },
  { to: "/contact", label: t.nav.contact, key: NAV.linkContact },
];

function Header() {
  const { lang, setLang, t, selectedScenarios } = useApp();
  const loc = useLocation();

  return (
    <header className="sticky top-0 z-50 backdrop-blur-2xl bg-black/60 border-b border-white/10">
      <div className="max-w-7xl mx-auto px-5 sm:px-10 h-16 flex items-center justify-between gap-6">
        <Link to="/" data-testid={NAV.logo} className="flex items-center gap-2 group">
          <div className="relative h-8 w-8 rounded-lg bg-amber-500/15 border border-amber-500/30 flex items-center justify-center">
            <Sparkles className="h-4 w-4 text-amber-400" strokeWidth={2.2} />
            <div className="absolute inset-0 rounded-lg pulse-glow" />
          </div>
          <span className="font-heading font-semibold tracking-tight text-lg">
            SmartLife<span className="text-amber-400">.</span>
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-1">
          {navLinks(t).map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={n.key}
              className={({ isActive }) =>
                `px-4 py-2 text-sm rounded-full transition-colors ${
                  isActive
                    ? "text-white bg-white/5"
                    : "text-gray-400 hover:text-white hover:bg-white/5"
                }`
              }
            >
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          {selectedScenarios.length > 0 && loc.pathname !== "/builder" && (
            <Link
              to="/builder"
              className="hidden sm:flex items-center gap-1.5 text-xs text-amber-300 bg-amber-500/10 border border-amber-500/30 px-3 py-1.5 rounded-full hover:bg-amber-500/15 transition-colors"
            >
              <ListChecks className="h-3.5 w-3.5" />
              {selectedScenarios.length}
            </Link>
          )}
          <button
            data-testid={NAV.langToggle}
            onClick={() => setLang(lang === "tr" ? "en" : "tr")}
            className="flex items-center gap-1.5 text-xs text-gray-300 hover:text-white border border-white/10 hover:border-white/25 px-3 py-1.5 rounded-full transition-colors"
          >
            <Globe className="h-3.5 w-3.5" />
            {lang.toUpperCase()}
          </button>
          <Link
            to="/contact"
            data-testid={NAV.ctaQuote}
            className="hidden sm:inline-flex bg-amber-500 hover:bg-amber-400 text-black text-sm font-semibold rounded-full px-5 py-2 transition-all hover:shadow-[0_0_20px_rgba(245,158,11,0.4)]"
          >
            {t.nav.cta}
          </Link>
        </div>
      </div>
      {/* mobile nav */}
      <nav className="md:hidden flex items-center justify-center gap-1 pb-2 px-4 border-t border-white/5 overflow-x-auto">
        {navLinks(t).map((n) => (
          <NavLink
            key={n.to}
            to={n.to}
            className={({ isActive }) =>
              `px-3 py-1.5 text-xs rounded-full whitespace-nowrap ${
                isActive ? "text-white bg-white/10" : "text-gray-400"
              }`
            }
          >
            {n.label}
          </NavLink>
        ))}
      </nav>
    </header>
  );
}

function Footer() {
  const { t } = useApp();
  return (
    <footer className="mt-32 border-t border-white/5">
      <div className="max-w-7xl mx-auto px-5 sm:px-10 py-12 grid md:grid-cols-3 gap-8">
        <div>
          <div className="flex items-center gap-2 mb-3">
            <Sparkles className="h-4 w-4 text-amber-400" />
            <span className="font-heading font-semibold">SmartLife.</span>
          </div>
          <p className="text-sm text-gray-400 leading-relaxed">{t.footer.tagline}</p>
        </div>
        <div className="text-sm text-gray-400 space-y-2">
          <div className="text-white font-medium mb-2 text-xs uppercase tracking-[0.2em]">
            {t.nav.scenarios}
          </div>
          <Link to="/scenarios" className="block hover:text-white transition-colors">
            {t.nav.scenarios}
          </Link>
          <Link to="/builder" className="block hover:text-white transition-colors">
            {t.nav.builder}
          </Link>
          <Link to="/contact" className="block hover:text-white transition-colors">
            {t.nav.contact}
          </Link>
        </div>
        <div className="text-sm text-gray-400">
          <div className="text-white font-medium mb-2 text-xs uppercase tracking-[0.2em]">
            Cihazpark
          </div>
          <p className="leading-relaxed">
            © {new Date().getFullYear()} SmartLife by Cihazpark. {t.footer.rights}
          </p>
        </div>
      </div>
    </footer>
  );
}

export default function Layout({ children }) {
  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <motion.main
        key={Math.random()}
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="flex-1"
      >
        {children}
      </motion.main>
      <Footer />
    </div>
  );
}
